/**
 * @file index.tsx
 *
 * @fileoverview Renders side block sub menu item.
 */

import { useContext, useMemo } from 'react';
import { MenuItem } from '~/jobiqo/types/types';
import Link from '~/jobiqo/components/Link';
import {
  SideBlockSubMenu,
  SideBlockSubMenuContentItem,
  SideBlockSubMenuItem,
} from '~/vcl/molecules/04-navigation/05-side-block-sub-menu';
import { getTranslatedLink, isUrlActive } from '~/jobiqo/utils/links';
import { AppContext, PathContext } from '~/jobiqo/context/app';
import { LayoutContext } from '~/jobiqo/context/layout';
import {
  getMenuIcon,
  renderIcon,
} from '~/jobiqo/components/Layout/components/MenuItemWrapper';

export interface LayoutSidebarSubMenuProps {
  /**
   * The menu item.
   */
  menuItem: MenuItem;
}

export function LayoutSidebarSubMenu({ menuItem }: LayoutSidebarSubMenuProps) {
  const { locale } = useContext(AppContext);
  const { path } = useContext(PathContext);
  const { getSidenavCollapsed, toggleSidenavCollapsed } = useContext(
    LayoutContext,
  );
  const collapsed = getSidenavCollapsed();
  const LinkIcon = useMemo(() => getMenuIcon(menuItem.class), [menuItem.class]);

  return (
    <SideBlockSubMenu>
      <SideBlockSubMenuItem
        Label={
          <>
            {renderIcon(
              menuItem,
              'side',
              LinkIcon,
              () => collapsed && toggleSidenavCollapsed(),
            )}
            <span>{menuItem.title}</span>
          </>
        }
        collapsed={collapsed}
        value={menuItem.title}
      >
        {menuItem.children?.map((item) => {
          if (!item) {
            return null;
          }

          const isActive = isUrlActive(item.url, path);
          const linkAs = getTranslatedLink(item.url.path, locale);
          const itemIcon = getMenuIcon(item.class);

          return (
            <SideBlockSubMenuContentItem
              key={item.title}
              active={isActive}
              Label={
                <Link href={item.url.path} as={linkAs}>
                  {item.title}
                </Link>
              }
              Icon={renderIcon(item, 'side', itemIcon) ?? undefined}
            />
          );
        })}
      </SideBlockSubMenuItem>
    </SideBlockSubMenu>
  );
}
